import React from 'react';

const OctoxDemoPage = ({
  logoSrc = process.env.PUBLIC_URL + '/images/TAKO-LOGO.png',
  backgroundTentaclePngSrc = process.env.PUBLIC_URL + '/images/2258.png',
  onPrimaryAction
}) => {
  const handlePrimaryAction = onPrimaryAction || (() => {});

  const renderBreakableText = (text) => {
    const parts = String(text).split(/([，、,。；;：:])/);
    return parts.map((part, idx) => {
      if (!part) return null;
      const isPunc = /^(?:[，、,。；;：:])$/.test(part);
      return (
        <React.Fragment key={idx}>
          {part}
          {isPunc ? <wbr /> : null}
        </React.Fragment>
      );
    });
  };

  return (
    <div
      className="min-h-screen bg-gray-900 text-gray-100 font-sans selection:bg-[#d4af37] selection:text-gray-900"
      style={{ fontFamily: "'Inter', sans-serif" }}
    >
      <style>{`
        @keyframes octo-float {
          0%, 100% { transform: translateY(0); }
          50% { transform: translateY(-20px); }
        }
        @keyframes shimmer {
          0% { transform: translateX(-150%) skewX(-12deg); }
          100% { transform: translateX(150%) skewX(-12deg); }
        }
        @keyframes tentacle-wave {
          0%, 100% { transform: rotate(-5deg) translateY(0) scale(1); }
          50% { transform: rotate(5deg) translateY(-20px) scale(1.1); }
        }
        .animate-octo-float { animation: octo-float 6s ease-in-out infinite; }
        .animate-shimmer { animation: shimmer 3s infinite; }
        .animate-tentacle-wave { animation: tentacle-wave 12s ease-in-out infinite; }
      `}</style>

      <header className="fixed w-full top-0 z-50 bg-gray-950/80 backdrop-blur-sm shadow-lg border-b border-white/5">
        <nav className="w-full max-w-[1440px] mx-auto px-4 sm:px-6 lg:px-8 py-4 flex justify-between items-center">
          <div className="text-2xl font-extrabold flex items-center gap-3">
            <img
              src={logoSrc}
              alt="OCTOX Logo"
              className="h-12 w-auto object-contain drop-shadow-[0_0_5px_rgba(212,175,55,0.5)] filter brightness-110 sepia-[.3] saturate-[1.5] hue-rotate-[-10deg]"
            />
            <div className="flex items-baseline">
              <span className="text-white">OCTO</span>
              <span className="text-yellow-400">X</span>
            </div>
          </div>

          <div className="hidden md:flex space-x-8 text-sm items-center font-medium text-gray-300">
            <span className="opacity-70">示範頁</span>
          </div>

          <button
            onClick={handlePrimaryAction}
            className="bg-[#d4af37] text-gray-900 px-5 py-2 rounded-full font-bold text-sm shadow-[0_0_15px_rgba(212,175,55,0.3)] hover:bg-[#e5c158] hover:shadow-[0_0_25px_rgba(212,175,55,0.5)] transition duration-300"
          >
            聯絡章魚無限
          </button>
        </nav>
      </header>

      <section className="relative min-h-screen flex items-center justify-center overflow-hidden pt-20 pb-0">
        <div className="absolute inset-0 bg-gray-900">
          <div className="absolute inset-0 bg-[radial-gradient(circle_at_center,_var(--tw-gradient-stops))] from-[#d4af37]/10 via-gray-900 to-gray-950"></div>

          <div className="absolute -left-[20%] -top-[20%] w-[100%] h-[150%] pointer-events-none opacity-[0.07] mix-blend-screen z-0 overflow-visible">
            <img
              src={backgroundTentaclePngSrc}
              alt="Background Kraken"
              className="w-full h-full object-contain filter invert sepia saturate-[3] hue-rotate-[10deg] animate-octo-float"
              style={{ transform: 'rotate(-45deg)' }}
            />
          </div>
        </div>

        <div className="relative z-10 w-full max-w-7xl mx-auto px-6 flex flex-col md:flex-row items-center justify-center gap-12 lg:gap-24">
          <div className="w-full max-w-xl flex flex-col items-center md:items-start text-center md:text-left">
            <div className="mb-6 relative group">
              <div className="absolute -inset-10 bg-[#d4af37] rounded-full blur-[80px] opacity-20 group-hover:opacity-30 transition duration-1000"></div>
              <img
                src={logoSrc}
                alt="OCTOX Logo"
                className="h-48 md:h-64 w-auto object-contain rounded-full drop-shadow-[0_0_30px_rgba(212,175,55,0.6)] mix-blend-screen filter brightness-110 contrast-125 sepia-[.5] hue-rotate-[5deg] saturate-[2]"
              />
            </div>

            <h1 className="text-5xl md:text-7xl font-black text-white mb-2 tracking-tighter drop-shadow-2xl">
              OCTO<span className="text-[#d4af37]">X</span>
            </h1>

            <h2 className="text-2xl md:text-3xl font-serif font-bold text-white mb-4 leading-tight drop-shadow-md">
              做地圖數據圖表找<span className="text-[#d4af37]">章魚無限</span>就對了
            </h2>

            <p className="text-gray-300 text-base max-w-md leading-relaxed mb-6">
              章魚無限可幫你把想法具現化
            </p>
          </div>

          <div className="w-full max-w-lg">
            <div className="relative group transform hover:scale-[1.02] transition-all duration-300">
              <div className="absolute -inset-1 bg-gradient-to-br from-blue-600 via-indigo-900 to-purple-900 rounded-2xl blur opacity-60 group-hover:opacity-90 transition duration-500 animate-pulse"></div>

              <div className="relative bg-gray-900/95 backdrop-blur-xl border border-blue-500/50 rounded-2xl p-8 flex flex-col justify-between min-h-[320px] shadow-2xl overflow-hidden">
                <div className="absolute inset-0 bg-gradient-to-r from-transparent via-white/5 to-transparent -translate-x-full animate-shimmer z-0"></div>

                <div className="absolute -right-16 -bottom-16 w-[130%] h-[130%] pointer-events-none z-0 animate-tentacle-wave opacity-20 mix-blend-screen">
                  <img
                    src={backgroundTentaclePngSrc}
                    alt="Octo Tentacle"
                    className="w-full h-full object-contain filter invert contrast-150"
                    style={{ transformOrigin: 'bottom right' }}
                  />
                </div>

                <div className="relative z-10">
                  <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-blue-500/20 text-blue-300 border border-blue-500/40 text-xs font-bold mb-4">
                    <span className="w-1.5 h-1.5 rounded-full bg-blue-400 animate-pulse"></span>
                    Data / Map / Chart
                  </div>

                  <h3 className="text-4xl md:text-5xl font-black text-white mb-4 leading-[1.1] tracking-tight">
                    地圖<br />
                    <span className="text-transparent bg-clip-text bg-gradient-to-r from-blue-400 to-indigo-300">數據圖表</span>
                  </h3>

                  <p className="text-gray-300 text-base font-medium leading-relaxed border-l-4 border-blue-500 pl-4">
                    {renderBreakableText('需求釐清、資料串接、視覺化呈現，一頁就能把重點講清楚。')}
                  </p>
                </div>

                <div className="pt-6 mt-auto relative z-10">
                  <button
                    onClick={handlePrimaryAction}
                    className="w-full flex items-center justify-center gap-3 bg-blue-600 text-white px-6 py-4 rounded-xl font-bold text-lg shadow-lg hover:bg-blue-500 transition-all group-hover:translate-x-1"
                  >
                    立即開始 ➔
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      <div className="fixed bottom-0 left-0 w-full h-[10vh] bg-black z-50 flex flex-col justify-center items-center border-t-2 border-[#d4af37] shadow-[0_-10px_40px_rgba(0,0,0,0.5)]">
        <div className="flex flex-row items-center justify-center space-x-4 md:space-x-12 text-white">
          <p className="text-[2.5vh] md:text-[4vh] font-black tracking-widest drop-shadow-md font-serif text-[#d4af37]">
            禁止酒駕
          </p>
          <div className="w-px h-[4vh] bg-[#d4af37]/50"></div>
          <p className="text-[2.5vh] md:text-[4vh] font-black tracking-widest drop-shadow-md font-serif text-[#d4af37]">
            未滿18歲禁止飲酒
          </p>
        </div>
      </div>

      <div className="h-[10vh]"></div>
    </div>
  );
};

export default OctoxDemoPage;
